/**
 * Newsletter subscribe — AJAX wiring for every form rendered by
 * templates/_partials/newsletter-form.twig.
 *
 * On submit:
 *  1. preventDefault — we own the navigation.
 *  2. Await the CSRF refresh utility so the hidden token in the form
 *     is current (Blitz-cached pages ship stale tokens).
 *  3. POST as FormData to the action URL embedded in the {{ actionInput() }}
 *     hidden field (Craft routes by that, not by the form's URL).
 *  4. Read the JSON response, render status into [data-newsletter-status].
 *  5. On success: clear the form fields and set the
 *     `newsletter_dismissed=subscribed` cookie for a year so the popup
 *     stops auto-opening. The surrounding <dialog> (if any) is NOT
 *     auto-closed — the user closes it themselves via × / ESC /
 *     backdrop so the success message stays visible as long as they
 *     want it.
 */

import refreshCsrfToken from './refresh-csrf-token.js';

const SELECTOR_FORM = '[data-newsletter-form]';
const SELECTOR_STATUS = '[data-newsletter-status]';
const COOKIE_NAME = 'newsletter_dismissed';
const COOKIE_DAYS = 365;
const snaptchaRefreshes = new WeakMap();

async function refreshSnaptchaField(form, { force = false } = {}) {
    const currentField = form.querySelector('input[name="snaptcha"]');
    if (currentField && currentField.value && !force) return true;

    try {
        const response = await fetch('/actions/snaptcha/field/get-field', {
            headers: { Accept: 'text/html' },
            credentials: 'same-origin',
        });
        if (!response.ok) return false;

        const wrapper = document.createElement('div');
        wrapper.innerHTML = await response.text();
        const nextField = wrapper.querySelector('input[name="snaptcha"]');
        if (!nextField) return false;

        if (currentField) {
            currentField.replaceWith(nextField);
        } else {
            const actionField = form.querySelector('input[name="action"]');
            if (actionField) {
                actionField.insertAdjacentElement('afterend', nextField);
            } else {
                form.prepend(nextField);
            }
        }

        return true;
    } catch (err) {
        console.warn('Snaptcha refresh failed:', err);
        return false;
    }
}

function queueSnaptchaRefresh(form, options) {
    const refresh = refreshSnaptchaField(form, options);
    snaptchaRefreshes.set(form, refresh);
    return refresh;
}

function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function setStatus(el, message, kind) {
    if (!el) return;
    el.textContent = message || '';
    el.classList.remove('text-primary', 'text-red-500', 'font-medium');
    if (!message) return;
    if (kind === 'success') {
        el.classList.add('text-primary', 'font-medium');
    } else if (kind === 'error') {
        el.classList.add('text-red-500', 'font-medium');
    }
}

async function handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const status = form.querySelector(SELECTOR_STATUS);
    const submitBtn = form.querySelector('button[type="submit"]');
    const originalLabel = submitBtn ? submitBtn.textContent : '';

    setStatus(status, '', null);

    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Subscribing…';
    }

    try {
        // Make sure the hidden CSRF input in the form has a fresh token
        // before we serialise the form into FormData. refreshCsrfToken.init()
        // is called on DOMContentLoaded from app.js; calling getTokenAsync()
        // here either waits for the in-flight fetch or resolves immediately
        // if it's already done. In both cases the utility's resolution
        // handler has updated the hidden inputs by the time we proceed.
        await refreshCsrfToken.getTokenAsync();
        await snaptchaRefreshes.get(form);

        if (!form.querySelector('input[name="snaptcha"]')?.value) {
            const isReady = await queueSnaptchaRefresh(form);
            if (!isReady) {
                setStatus(status, 'Network error. Please try again.', 'error');
                return;
            }
        }

        const formData = new FormData(form);

        // Note: `form.action` (the property) is shadowed by the
        // <input name="action"> that {{ actionInput() }} renders — reading
        // it returns the input element, not the URL. Read the attribute
        // explicitly; Craft routes via the hidden `action` POST param so
        // posting to the current URL is correct.
        const actionUrl = form.getAttribute('action') || window.location.href;
        const response = await fetch(actionUrl, {
            method: 'POST',
            body: formData,
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        });

        let data = null;
        try {
            data = await response.json();
        } catch (e) {
            data = null;
        }

        if (response.ok && data && data.success) {
            setStatus(status, data.message || 'Thanks for subscribing!', 'success');
            form.reset();
            // Stop the popup from re-bothering this visitor. The surrounding
            // <dialog> (if any) stays open so the success message remains
            // visible — the user closes it themselves via × / ESC / backdrop.
            setCookie(COOKIE_NAME, 'subscribed', COOKIE_DAYS);
        } else {
            const msg = (data && data.message) || 'Something went wrong. Please try again.';
            setStatus(status, msg, 'error');
        }
    } catch (err) {
        console.error('Newsletter subscribe failed:', err);
        setStatus(status, 'Network error. Please try again.', 'error');
    } finally {
        await queueSnaptchaRefresh(form, { force: true });

        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalLabel;
        }
    }
}

export default {
    init() {
        document.querySelectorAll(SELECTOR_FORM).forEach((form) => {
            form.addEventListener('submit', handleSubmit);
            queueSnaptchaRefresh(form, { force: true });
        });
    },
};
